import React, { ClipboardEvent, useEffect, useRef, useState, DragEvent } from 'react';
import '@blueprintjs/table/lib/css/table.css';
import '@blueprintjs/popover2/lib/css/blueprint-popover2.css';
import { FieldProps } from 'formik';
import { HotkeysProvider } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import {
    parseCsvToData,
    shouldAutoImportCsv,
    convertCsvToTableInputRows,
    parseTabDelimitedPaste,
    ICsvData,
} from './validation';
import { normalizeColumnName } from './columnUtils';
import { DocumentIcon, ArrowPathIcon, TrashIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { CellProps, Column, FocusedCellCoordinates, Region, Table2 } from '@blueprintjs/table';
import {
    getInitialTableInputValue,
    calculateDefaultColumnWidths,
    loadTableDefaultValue,
    convertExcelToTableInputRows,
    hasNonEmptyRows,
} from './utils';
import TextAreaCell from '../SequenceInput/TextAreaCell';
import DropdownCell from './DropdownCell';
import { resizeRowsByApproximateHeight } from '@blueprintjs/table/lib/esnext/resizeRows';
import ResizableDiv from '../SequenceInput/ResizableDiv';
import PasteModal, { PasteType } from '../SequenceInput/PasteModal';
import { ITableInputValue } from '../types';
import { ISetting } from '@biolibtech/biolib-js';
import { UtilityButton } from '@components/atoms/UtilityButton';
import { useGetDataRecordVersionQuery } from '@apiHooks';
import { downloadFile } from '@features/FileRenderer/utils';
import { TemplatesDropdown } from './TemplatesDropdown';
import { parseExcelInIframe, IExcelFile } from './parseExcelInIframe';
import ImportModal from './ImportModal';
import { shouldAutoImportExcel } from './excelUtils';

const ACCEPTED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

const replaceExtensionWithCsv = (filename: string): string => {
    for (const ext of ACCEPTED_FILE_EXTENSIONS) {
        if (filename.toLowerCase().endsWith(ext)) {
            return filename.slice(0, -ext.length) + '.csv';
        }
    }
    return filename;
};

interface IProps extends FieldProps {
    autoFocus: boolean;
    setting: ISetting;
    resourceVersionUuid: string;
}

export default function TableInput(props: IProps) {
    const { rows, filename } = props.field.value as ITableInputValue;
    const defaultValue = props.setting?.default_value;
    const columnConfigs = props.setting.config?.columns as any[] | undefined;
    const columnNames: string[] = columnConfigs?.map((col: any) => col.name) || [];
    const normalizePastedValue = (value: string, columnIndex: number): string => {
        const trimmedValue = value.trim();
        return columnConfigs?.[columnIndex]?.type === 'number' ? trimmedValue.replace(/\s+/g, '') : trimmedValue;
    };
    const tableRef = useRef<Table2>();
    const containerRef = useRef<HTMLDivElement>();
    const fileInputRef = useRef<HTMLInputElement>();
    const [enableFocusedCell, setEnableFocusedCell] = useState<boolean>(props.autoFocus);
    const [focusedCell, setFocusedCell] = useState<FocusedCellCoordinates>({ row: 0, col: 0, focusSelectionIndex: 0 });
    const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
    const [isLocked, setIsLocked] = useState<boolean>(false);
    const [tableRenderInvalidator, setTableRenderInvalidator] = useState<boolean>(false);
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const [columnWidths, setColumnWidths] = useState<number[]>(() => Array(columnNames.length).fill(200));
    const [rowHeights, setRowHeights] = useState<number[]>([]);
    const [adaptiveHeight, setAdaptiveHeight] = useState<number>(95);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [pastedTextForModal, setPastedTextForModal] = useState<string | null>(null);
    const [defaultCsvData, setDefaultCsvData] = useState<string | null>(null);
    const [excelImportData, setExcelImportData] = useState<IExcelFile | null>(null);
    const [csvImportData, setCsvImportData] = useState<ICsvData | null>(null);
    const [importFilename, setImportFilename] = useState<string | null>(null);
    const [showImportModal, setShowImportModal] = useState<boolean>(false);
    const fillDragRef = useRef<{ sourceRow: number; sourceCol: number; value: string; endRow: number } | null>(null);
    const fillDragListenersRef = useRef<{ move: (event: MouseEvent) => void; up: () => void } | null>(null);
    const [fillDragEndRow, setFillDragEndRow] = useState<number | null>(null);
    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    useEffect(() => {
        return () => {
            if (fillDragListenersRef.current) {
                document.removeEventListener('mousemove', fillDragListenersRef.current.move);
                document.removeEventListener('mouseup', fillDragListenersRef.current.up);
                fillDragListenersRef.current = null;
            }
            fillDragRef.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, []);

    const { data: versionData } = useGetDataRecordVersionQuery({ resourceVersionUuid: props.resourceVersionUuid });

    const templates = props.setting.config?.templates as string[] | undefined;

    const handleTemplateDownload = (templatePath: string) => {
        if (!versionData?.presigned_download_url) {
            console.error('Failed to download template: presigned URL not available');
            return;
        }

        const assetPrefix = 'assets://';
        const path = templatePath.startsWith(assetPrefix) ? templatePath.slice(assetPrefix.length) : templatePath;
        downloadFile(versionData.presigned_download_url, path);
    };

    const [failedToLoadCSVError, setFailedToLoadCSVError] = useState<string | null>(null);

    useEffect(() => {
        if (!defaultValue || defaultCsvData !== null) {
            return;
        }

        const loadDefaultValue = async () => {
            try {
                const csvData = await loadTableDefaultValue(props.resourceVersionUuid, defaultValue);
                const tableInputValue = getInitialTableInputValue(csvData, columnConfigs);
                props.form.setFieldValue(props.field.name, tableInputValue, true);
                setDefaultCsvData(csvData);
            } catch (error: any) {
                setFailedToLoadCSVError(
                    `Failed to load default values. Please input data manually. Got error: ${error.message}`,
                );
                console.error(error);
            }
        };

        loadDefaultValue();
    }, [defaultValue]);

    const applyDefaultColumnWidths = () => {
        const defaultWidths = calculateDefaultColumnWidths(columnNames, tableRef, containerWidth - 5); // -5 for padding
        setColumnWidths(defaultWidths);
    };

    const clearTable = () => {
        props.form.setFieldTouched(props.field.name, false, false);
        props.form.setFieldValue(props.field.name, getInitialTableInputValue(undefined, columnConfigs), true);
        props.form.setFieldError(props.field.name, undefined);
        setTableRenderInvalidator(!tableRenderInvalidator);
        setIsLocked(false);
        applyDefaultColumnWidths();
    };

    const resetTable = () => {
        props.form.setFieldTouched(props.field.name, false, false);
        props.form.setFieldValue(props.field.name, getInitialTableInputValue(defaultCsvData, columnConfigs), true);
        props.form.setFieldError(props.field.name, undefined);
        setTableRenderInvalidator(!tableRenderInvalidator);
        setIsLocked(false);
        applyDefaultColumnWidths();
    };

    useEffect(() => {
        if (!rows) {
            return;
        }
        const rowHeights: number[] = resizeRowsByApproximateHeight(
            Object.keys(rows).length,
            columnWidths,
            (rowIndex: number, columnIndex: number) => rows[rowIndex][columnIndex] || '-',
            {
                getApproximateCharWidth: 7.5,
                getApproximateLineHeight: 20,
                getCellHorizontalPadding: 7,
                getNumBufferLines: 0,
            },
        );
        for (let i = 0; i < rowHeights.length; i += 1) {
            rowHeights[i] += 2; // add top and bottom padding;
        }
        setRowHeights(rowHeights);
        setTableRenderInvalidator(!tableRenderInvalidator);

        const headerHeight = 30;
        const totalRowHeight = rowHeights.reduce((sum, h) => sum + h, 0);
        setAdaptiveHeight(Math.max(headerHeight + totalRowHeight, 95));
    }, [containerWidth, rows, columnWidths]);

    useEffect(() => {
        applyDefaultColumnWidths();
    }, [tableRef.current, containerWidth]);

    const updateRows = (rowsToUpdate: Record<number, Record<number, string>>, forceTableReRender: boolean) => {
        const newRows = { ...rowsRef.current, ...rowsToUpdate };
        const newRowsLength = Object.keys(newRows).length;
        const lastRow = newRows[newRowsLength - 1];
        const numColumns = columnNames.length;

        const hasAnyValue = Object.values(lastRow).some((value) => value?.trim());
        if (hasAnyValue) {
            const newEmptyRow: Record<number, string> = {};
            for (let i = 0; i < numColumns; i++) {
                newEmptyRow[i] = '';
            }
            newRows[newRowsLength] = newEmptyRow;
        }
        props.form.setFieldValue(props.field.name, { filename, rows: newRows }, true);
        if (forceTableReRender) {
            setTableRenderInvalidator(!tableRenderInvalidator);
        }
    };

    useEffect(() => {
        const onResize = () => {
            if (containerRef.current) {
                setContainerWidth(Math.max(containerRef.current.offsetWidth, 100));
            }
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const cellRenderer = (rowIndex: number, columnIndex: number): React.ReactElement<CellProps> => {
        const cellValue = rows[rowIndex]?.[columnIndex] ?? '';
        const columnConfig = columnConfigs?.[columnIndex];
        const isFocused = focusedCell.row === rowIndex && focusedCell.col === columnIndex;

        if (columnConfig?.type === 'dropdown' && columnConfig.options) {
            return (
                <DropdownCell
                    value={cellValue}
                    setValue={(newValue) => {
                        props.form.setFieldTouched(props.field.name, true, true);
                        updateRows({ [rowIndex]: { ...rows[rowIndex], [columnIndex]: newValue } }, false);
                    }}
                    options={columnConfig.options}
                    readOnly={isLocked}
                    isFocused={enableFocusedCell && isFocused && !isLocked}
                />
            );
        }
        const showFillHandle = enableFocusedCell && isFocused && !isLocked && cellValue.trim() !== '';
        const isFillTarget =
            fillDragRef.current !== null &&
            fillDragEndRow !== null &&
            columnIndex === fillDragRef.current.sourceCol &&
            rowIndex > fillDragRef.current.sourceRow &&
            rowIndex <= fillDragEndRow;

        return (
            <TextAreaCell
                value={cellValue}
                onFocus={() => {
                    if (
                        rowIndex === 0 &&
                        rowIndex === focusedCell.row &&
                        columnIndex === 0 &&
                        columnIndex === focusedCell.col
                    ) {
                        // initial focus do nothing
                    } else {
                        props.form.setFieldTouched(props.field.name, true, true);
                    }
                }}
                setValue={(newValue) => {
                    updateRows({ [rowIndex]: { ...rows[rowIndex], [columnIndex]: newValue } }, false);
                }}
                readOnly={isLocked}
                onFillHandleMouseDown={
                    showFillHandle ? (event) => handleFillDragStart(event, rowIndex, columnIndex) : undefined
                }
                isFillTarget={isFillTarget}
            />
        );
    };

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
        // Strip only leading/trailing newlines, not tabs/spaces which represent empty cells
        const rawText = event.clipboardData.getData('Text').replace(/^[\r\n]+|[\r\n]+$/g, '');
        if (!focusedCell) {
            return;
        }

        if (rawText.includes('\t')) {
            const pastedRows = parseTabDelimitedPaste(rawText);

            let hasHeaderRow = false;
            if (pastedRows.length > 1) {
                const firstRowValues = pastedRows[0];
                hasHeaderRow = firstRowValues.length > 0 && firstRowValues.every((value, j) => {
                    const targetColumnIndex = focusedCell.col + j;
                    // TODO: use allow_additional_columns config when that feature is introduced
                    if (targetColumnIndex >= columnNames.length) {
                        return false;
                    }
                    return normalizeColumnName(value) === normalizeColumnName(columnNames[targetColumnIndex]);
                });
            }

            const rowsToUpdate: Record<number, Record<number, string>> = {};
            const dataStartIndex = hasHeaderRow ? 1 : 0;
            for (let i = dataStartIndex; i < pastedRows.length; i += 1) {
                const pastedColumns = pastedRows[i];
                const newRowIndex = focusedCell.row + (i - dataStartIndex);
                rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};

                for (let j = 0; j < pastedColumns.length; j += 1) {
                    const targetColumnIndex = focusedCell.col + j;
                    if (targetColumnIndex < columnNames.length) {
                        rowsToUpdate[newRowIndex][targetColumnIndex] = normalizePastedValue(
                            pastedColumns[j] ?? '',
                            targetColumnIndex,
                        );
                    }
                }
            }
            event.preventDefault();
            updateRows(rowsToUpdate, true);
        } else if (rawText.includes('\n')) {
            // Parse multiline text
            event.preventDefault();
            event.stopPropagation();
            setPastedTextForModal(rawText);
        } else {
            event.preventDefault();
            event.stopPropagation();
            const value = normalizePastedValue(rawText, focusedCell.col);
            const rowsToUpdate: Record<number, Record<number, string>> = {
                [focusedCell.row]: { ...rows[focusedCell.row], [focusedCell.col]: value },
            };
            updateRows(rowsToUpdate, true);
        }
    };

    const handleImport = (sheetName: string | null, columnMapping: Record<number, number>, hasHeaders: boolean) => {
        try {
            let importedRows: Record<number, Record<number, string>>;
            let importedFilename: string;

            if (excelImportData && sheetName) {
                importedRows = convertExcelToTableInputRows(
                    excelImportData,
                    columnConfigs,
                    sheetName,
                    columnMapping,
                    hasHeaders,
                );
                importedFilename = importFilename || 'input.csv';
            } else if (csvImportData) {
                importedRows = convertCsvToTableInputRows(csvImportData, columnConfigs, columnMapping, hasHeaders);
                importedFilename = importFilename || 'input.csv';
            } else {
                return;
            }

            if (!hasNonEmptyRows(importedRows)) {
                props.form.setFieldTouched(props.field.name, true, false);
                props.form.setFieldError(
                    props.field.name,
                    'The uploaded file is empty. It has matching columns but no data rows.',
                );
                return;
            }

            setTimeout(() => applyDefaultColumnWidths(), 0);

            props.form.setFieldValue(
                props.field.name,
                {
                    filename: importedFilename,
                    rows: {
                        ...getInitialTableInputValue(undefined, columnConfigs).rows,
                        ...importedRows,
                    },
                },
                true,
            );
            setTableRenderInvalidator(!tableRenderInvalidator);
            tableRef.current?.scrollToRegion({ rows: [0, 0] });
        } catch (error) {
            props.form.setFieldTouched(props.field.name, true, false);
            props.form.setFieldError(
                props.field.name,
                `Failed to import ${excelImportData ? 'Excel' : 'CSV'} data. ${error.message}`,
            );
        }
    };

    const onFileSelect = async (files: FileList) => {
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];
        const fileName = file.name.toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

        if (isExcel) {
            try {
                const arrayBuffer = await file.arrayBuffer();
                const parsed = await parseExcelInIframe(arrayBuffer);

                const autoImport = shouldAutoImportExcel(parsed, columnNames);

                if (autoImport.shouldAutoImport) {
                    const excelRows = convertExcelToTableInputRows(
                        parsed,
                        columnConfigs,
                        autoImport.sheetName,
                        autoImport.columnMapping,
                        autoImport.hasHeaders,
                    );

                    if (!hasNonEmptyRows(excelRows)) {
                        props.form.setFieldTouched(props.field.name, true, false);
                        props.form.setFieldError(
                            props.field.name,
                            'The uploaded file is empty. It has matching columns but no data rows.',
                        );
                        return;
                    }

                    setTimeout(() => applyDefaultColumnWidths(), 0);

                    props.form.setFieldValue(
                        props.field.name,
                        {
                            filename: replaceExtensionWithCsv(file.name),
                            rows: {
                                ...getInitialTableInputValue(undefined, columnConfigs).rows,
                                ...excelRows,
                            },
                        },
                        true,
                    );
                    setTableRenderInvalidator(!tableRenderInvalidator);
                    tableRef.current?.scrollToRegion({ rows: [0, 0] });
                } else {
                    setExcelImportData(parsed);
                    setCsvImportData(null);
                    setImportFilename(replaceExtensionWithCsv(file.name));
                    setShowImportModal(true);
                }
            } catch (error) {
                props.form.setFieldTouched(props.field.name, true, false);
                props.form.setFieldError(props.field.name, `Failed to parse file as Excel. ${error.message}`);
            }
        } else {
            const text = await file.text();

            try {
                const csvData = parseCsvToData(text);
                const autoImport = shouldAutoImportCsv(csvData, columnNames);

                if (autoImport.shouldAutoImport) {
                    const csvRows = convertCsvToTableInputRows(
                        csvData,
                        columnConfigs,
                        autoImport.columnMapping,
                        autoImport.hasHeaders,
                    );

                    if (!hasNonEmptyRows(csvRows)) {
                        props.form.setFieldTouched(props.field.name, true, false);
                        props.form.setFieldError(
                            props.field.name,
                            'The uploaded file is empty. It has matching columns but no data rows.',
                        );
                        return;
                    }

                    setTimeout(() => applyDefaultColumnWidths(), 0);

                    props.form.setFieldValue(
                        props.field.name,
                        {
                            filename: file.name,
                            rows: {
                                ...getInitialTableInputValue(undefined, columnConfigs).rows,
                                ...csvRows,
                            },
                        },
                        true,
                    );
                    setTableRenderInvalidator(!tableRenderInvalidator);
                    tableRef.current?.scrollToRegion({ rows: [0, 0] });
                } else {
                    setExcelImportData(null);
                    setCsvImportData(csvData);
                    setImportFilename(file.name);
                    setShowImportModal(true);
                }
            } catch (error) {
                props.form.setFieldTouched(props.field.name, true, false);
                props.form.setFieldError(props.field.name, `Failed to parse file as CSV. ${error.message}`);
            }
        }
    };

    const deleteSelectedRegions = () => {
        const rowsToUpdate: Record<number, Record<number, string>> = {};
        for (const region of selectedRegions) {
            const {
                rows: [rowStart, rowEnd],
                cols: [colStart, colEnd],
            } = selectedRegions[0];
            for (let i = rowStart; i < rowEnd + 1; i += 1) {
                rowsToUpdate[i] = rows[i];
                for (let j = colStart; j < colEnd + 1; j += 1) {
                    rowsToUpdate[i][j] = '';
                }
            }
        }
        updateRows(rowsToUpdate, true);
    };

    const handleColumnWidthChanged = (index: number, size: number) => {
        const newColumnWidths = [...columnWidths];
        newColumnWidths[index] = size;
        setColumnWidths(newColumnWidths);
    };

    const handleFillDragStart = (event: React.MouseEvent, row: number, col: number) => {
        event.preventDefault();
        event.stopPropagation();
        const value = rows[row]?.[col];
        if (!value?.trim()) return;

        fillDragRef.current = { sourceRow: row, sourceCol: col, value, endRow: row };
        setFillDragEndRow(row);

        const startY = event.clientY;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (!fillDragRef.current) return;
            const deltaY = moveEvent.clientY - startY;

            let targetRow = fillDragRef.current.sourceRow;
            if (deltaY > 0) {
                let accumulatedHeight = 0;
                for (let i = fillDragRef.current.sourceRow + 1; i < rowHeights.length; i++) {
                    accumulatedHeight += rowHeights[i];
                    targetRow = i;
                    if (accumulatedHeight >= deltaY) break;
                }
            }

            const endRow = Math.max(targetRow, fillDragRef.current.sourceRow);
            fillDragRef.current.endRow = endRow;
            setFillDragEndRow(endRow);
        };

        const handleMouseUp = () => {
            if (!fillDragListenersRef.current) return;
            if (fillDragRef.current && fillDragRef.current.endRow > fillDragRef.current.sourceRow) {
                const { sourceRow, sourceCol, value: fillValue, endRow } = fillDragRef.current;
                const rowsToUpdate: Record<number, Record<number, string>> = {};
                for (let i = sourceRow + 1; i <= endRow; i++) {
                    rowsToUpdate[i] = { ...(rowsRef.current[i] || {}), [sourceCol]: fillValue };
                }
                updateRows(rowsToUpdate, true);
            }
            fillDragRef.current = null;
            setFillDragEndRow(null);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            fillDragListenersRef.current = null;
        };

        fillDragListenersRef.current = { move: handleMouseMove, up: handleMouseUp };
        document.body.style.cursor = 'crosshair';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);

        if (isLocked) {
            return;
        }

        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            if (event.dataTransfer.files.length > 1) {
                props.form.setFieldTouched(props.field.name, true, false);
                props.form.setFieldError(props.field.name, 'Only one file allowed. Please drag and drop a single file');
            } else {
                onFileSelect(event.dataTransfer.files);
            }
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (isLocked) return;
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
        }
        setIsDragging(false);
    };

    const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (isLocked) return;
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    return (
        <div>
            <ImportModal
                isOpen={showImportModal}
                onClose={() => {
                    setShowImportModal(false);
                    setExcelImportData(null);
                    setCsvImportData(null);
                }}
                onImport={handleImport}
                excelData={excelImportData}
                csvData={csvImportData}
                expectedColumns={columnNames}
            />
            <div className='flex flex-col md:flex-row w-full space-y-2 md:space-y-0 md:space-x-2'>
                <PasteModal
                    isOpen={!!pastedTextForModal}
                    columnName={columnNames[focusedCell.col] || `Column ${focusedCell.col + 1}`}
                    onClose={(pasteType) => {
                        if (pasteType === PasteType.multiple) {
                            const pastedRows = pastedTextForModal?.split('\n') || [];
                            const rowsToUpdate: Record<number, Record<number, string>> = {};
                            for (let i = 0; i < pastedRows.length; i += 1) {
                                const newRowIndex = focusedCell.row + i;
                                rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                                rowsToUpdate[newRowIndex][focusedCell.col] = normalizePastedValue(
                                    pastedRows[i],
                                    focusedCell.col,
                                );
                            }
                            updateRows(rowsToUpdate, true);
                        } else if (pasteType === PasteType.single) {
                            const newRowIndex = focusedCell.row;
                            const rowsToUpdate: Record<number, Record<number, string>> = {};
                            rowsToUpdate[newRowIndex] = newRowIndex in rows ? { ...rows[newRowIndex] } : {};
                            rowsToUpdate[newRowIndex][focusedCell.col] = normalizePastedValue(
                                pastedTextForModal ?? '',
                                focusedCell.col,
                            );
                            updateRows(rowsToUpdate, true);
                        }
                        setPastedTextForModal(null);
                    }}
                />
                <div
                    className={clsx(
                        'w-full rounded-md overflow-hidden border relative',
                        isDragging ? 'border-emerald-500' : isLocked ? 'border-gray-300 bg-gray-50' : 'border-gray-300',
                    )}
                    onPasteCapture={handlePaste}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDragEnter={handleDragEnter}
                    ref={containerRef}
                    onMouseEnter={() => {
                        document.documentElement.style.overscrollBehaviorX = 'none';
                        setEnableFocusedCell(!isLocked);
                    }}
                    onMouseLeave={() => (document.documentElement.style.overscrollBehaviorX = '')}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                        if (isLocked) return;

                        if (event.key === 'Backspace') {
                            if (selectedRegions.length === 1) {
                                const { rows, cols } = selectedRegions[0];
                                const rowCount = rows[1] - rows[0];
                                const colCount = cols[1] - cols[0];
                                if (rowCount > 0 || colCount > 0) {
                                    deleteSelectedRegions();
                                }
                            } else if (selectedRegions.length > 1) {
                                deleteSelectedRegions();
                            }
                        } else if (event.key === 'Delete') {
                            deleteSelectedRegions();
                        }
                    }}
                >
                    {isLocked && filename && (
                        <div className='z-40 mr-1 absolute top-0 right-0 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-bl-md border-l border-b border-gray-300'>
                            <span className='font-medium'>File: </span>
                            <span className='italic'>{filename}</span>
                        </div>
                    )}
                    {isDragging && (
                        <div className='absolute inset-0 bg-emerald-50 bg-opacity-70 flex items-center justify-center z-10'>
                            <div className='text-center'>
                                <DocumentIcon className='h-10 w-10 text-emerald-500 mx-auto' />
                                <p className='text-lg text-emerald-600 select-none my-auto'>
                                    <b>Drop file here</b>
                                </p>
                            </div>
                        </div>
                    )}
                    {fillDragEndRow !== null && (
                        <div className='absolute inset-0 z-30' style={{ cursor: 'crosshair' }} />
                    )}
                    <ResizableDiv initialHeight={adaptiveHeight} minHeight={95}>
                        <HotkeysProvider>
                            <Table2
                                cellRendererDependencies={[tableRenderInvalidator, fillDragEndRow, focusedCell.row, focusedCell.col]}
                                columnWidths={columnWidths}
                                enableColumnHeader={true}
                                enableColumnResizing={true}
                                enableFocusedCell={enableFocusedCell && !isLocked}
                                enableRowHeader={false}
                                focusedCell={isLocked ? undefined : focusedCell}
                                getCellClipboardData={(row: number, col: number) => rows[row]?.[col] ?? ''}
                                key={!containerWidth ? 'loading' : 'loaded'}
                                numRows={rowHeights.length}
                                onFocusedCell={isLocked ? undefined : setFocusedCell}
                                onSelection={
                                    isLocked ? undefined : (selectedRegions) => setSelectedRegions(selectedRegions)
                                }
                                ref={tableRef}
                                rowHeights={rowHeights}
                                selectedRegions={isLocked ? [] : selectedRegions}
                                onColumnWidthChanged={handleColumnWidthChanged}
                            >
                                {columnNames.map((name, index) => {
                                    const columnConfig = columnConfigs?.[index];
                                    const description = columnConfig?.description;

                                    return (
                                        <Column
                                            key={index}
                                            name='' // Note: setting this to empty string to avoid column header getting title A, B, C, etc.
                                            cellRenderer={cellRenderer}
                                            nameRenderer={() => (
                                                <div className='flex items-center space-x-1'>
                                                    <span
                                                        style={{ marginLeft: -2, fontSize: 12 }}
                                                        className='text-gray-600 font-bold overflow-hidden text-ellipsis whitespace-nowrap'
                                                    >
                                                        {name}
                                                    </span>
                                                    {description && (
                                                        <Tooltip2 content={description} placement='top'>
                                                            <span
                                                                className='inline-flex items-center cursor-help'
                                                                style={{ pointerEvents: 'auto' }}
                                                            >
                                                                <QuestionMarkCircleIcon className='size-3 text-gray-400 hover:text-gray-600' />
                                                            </span>
                                                        </Tooltip2>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    );
                                })}
                            </Table2>
                        </HotkeysProvider>
                    </ResizableDiv>
                </div>
                <div className='flex-none md:w-40 lg:w-36'>
                    <input
                        hidden={true}
                        multiple={false}
                        ref={fileInputRef}
                        type='file'
                        accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
                        onChange={(event) => {
                            onFileSelect(event.target.files);
                            if (fileInputRef?.current) {
                                fileInputRef.current.value = '';
                            }
                        }}
                    />
                    <div className='justify-between space-y-2'>
                        <UtilityButton
                            disabled={isLocked}
                            onClick={() => {
                                if (fileInputRef?.current) {
                                    fileInputRef.current.click();
                                }
                            }}
                        >
                            <DocumentIcon className='h-4 w-4 my-auto mr-1' aria-hidden='true' />
                            Upload file
                        </UtilityButton>
                        {templates && templates.length > 0 && (
                            <TemplatesDropdown templates={templates} onTemplateDownload={handleTemplateDownload} />
                        )}
                        {defaultCsvData ? (
                            <UtilityButton disabled={false} onClick={resetTable}>
                                <ArrowPathIcon className='h-4 w-4 my-auto mr-1' aria-hidden='true' />
                                Reset Table
                            </UtilityButton>
                        ) : (
                            <UtilityButton disabled={false} onClick={clearTable}>
                                <TrashIcon className='h-4 w-4 my-auto mr-1' aria-hidden='true' />
                                Clear Table
                            </UtilityButton>
                        )}
                    </div>
                </div>
            </div>
            {failedToLoadCSVError && (
                <span
                    style={{ color: '#ac2f33' }} // Blueprint error color
                    className='text-xs'
                >
                    {failedToLoadCSVError}
                </span>
            )}
        </div>
    );
}
